import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import emailjs from '@emailjs/browser';

const BookForm = () => {
  const navigate = useNavigate();
  const form = useRef();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', or null

  // EmailJS Configuration - Replace these with your actual values
  const EMAILJS_SERVICE_ID = 'service_mv2zzvl'; // Your EmailJS service ID
  const EMAILJS_TEMPLATE_ID = 'template_zgg6aus'; // Your EmailJS template ID
  const EMAILJS_PUBLIC_KEY = 'KYfInOepNwgX0jnkX'; // Your EmailJS public key

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    // Extract selected service name before resetting
    const serviceSelect = form.current ? form.current.elements['service'] : null;
    const selectedService = serviceSelect ? serviceSelect.value : '';

    try {
      console.log('📤 开始发送邮件...');
      console.log('配置信息:', {
        serviceId: EMAILJS_SERVICE_ID,
        templateId: EMAILJS_TEMPLATE_ID,
        publicKey: EMAILJS_PUBLIC_KEY
      });

      // Send email using EmailJS
      const result = await emailjs.sendForm(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        form.current,
        EMAILJS_PUBLIC_KEY
      );

      console.log('✅ 邮件发送成功:', result);
      console.log('状态:', result.status);
      console.log('响应:', result.text);
      setSubmitStatus('success');

      // Track booking conversion success in Google Tag Manager dataLayer
      if (window.dataLayer) {
        window.dataLayer.push({
          event: 'booking_conversion_success',
          formType: 'consultation_booking',
          timestamp: new Date().toISOString()
        });
      }
      
      // Reset form after successful submission
      form.current.reset();

      // Navigate immediately to thank-you / booking-success page
      navigate('/booking-success', { state: { serviceName: selectedService } });
      
    } catch (error) {
      console.error('❌ 邮件发送失败:', error);
      console.error('错误详情:', {
        status: error.status,
        text: error.text,
        message: error.message
      });
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-softGradient bg-no-repeat bg-top text-text">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-section">
        {/* 页面标题 */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4 text-gray-900">Online Booking</h1>
          <p className="text-xl text-gray-700">
            Fill in the information below and we will contact you soon
          </p>
        </div>

        {/* 预约表单 */}
        <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-card p-8 border border-champagne/40">

          {/* Success Alert */}
          {submitStatus === 'success' && (
            <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg">
              <div className="flex items-center">
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="font-medium">Success!</span>
              </div>
              <p className="mt-1">Your booking request has been sent successfully. We'll contact you soon!</p>
            </div>
          )}

          {/* Error Alert */}
          {submitStatus === 'error' && (
            <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
              <div className="flex items-center">
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <span className="font-medium">Error!</span>
              </div>
              <p className="mt-1">Failed to send your booking request. Please try again or contact us directly.</p>
            </div>
          )}

          {/* Form */}
          <form ref={form} onSubmit={handleSubmit} className="space-y-6">
            {/* Name Field */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                Name *
              </label>
              <input
                type="text"
                id="name"
                name="user_name"
                required
                placeholder="Enter your full name"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
              />
            </div>

            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                name="user_email"
                placeholder="Enter your email address (optional)"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
              />
            </div>

            {/* Phone Field */}
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                Phone Number *
              </label>
              <input
                type="tel"
                id="phone"
                name="user_phone"
                required
                placeholder="Enter your phone number"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
              />
            </div>

            {/* Service Field */}
            <div>
              <label htmlFor="service" className="block text-sm font-medium text-gray-700 mb-1">
                Service *
              </label>
              <select
                id="service"
                name="service"
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
              >
                <option value="">Please select a service</option>
                <option value="Consult about Injection Beauty">Consult about Injection Beauty</option>
                <option value="Consult about Skin Care">Consult about Skin Care</option>
                <option value="Consult about Body Shaping">Consult about Body Shaping</option>
                <option value="Ulthera Prime">Ulthera Prime</option>
                <option value="PicoSure Pro">PicoSure Pro</option>
                <option value="Sculptra">Sculptra</option>
                <option value="HydraFacial">HydraFacial</option>
                <option value="Radiesse">Radiesse</option>
                <option value="BBL">BBL (BroadBand Light)</option>
                <option value="Thermage FLX">Thermage FLX</option>
                <option value="Potenza">Potenza</option>
                <option value="Visia">Visia</option>
                <option value="Spectrum">Spectrum</option>
                <option value="Other">Other</option>
              </select>
            </div>


            {/* Message Field */}
            <div>
              <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                Additional Notes / Special Requirements
              </label>
              <textarea
                id="message"
                name="message"
                rows={4}
                placeholder="Please enter any special requirements, questions, or additional information..."
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
              />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className={`w-full py-3 rounded-xl shadow-soft transition-all text-gray-900 hover:shadow-glow hover:brightness-110 border border-champagne/60 ${
                isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              style={{
                backgroundImage:
                  'linear-gradient(90deg, rgba(217,195,169,1) 0%, rgba(217,195,169,0.8) 40%, rgba(217,195,169,0.3) 100%)'
              }}
            >
              {isSubmitting ? 'Sending...' : 'Send Booking Request'}
            </button>
          </form>

        </div>
      </div>
    </div>
  );
};

export default BookForm;
